import { isRecordableFrame } from '@streamr/tv-engine/~/recording';
import { EventEmitter } from 'eventemitter3';
import { MediaPacketFragmenter } from './MediaPacketFragmenter';
import { createDoneEncodedAudioFrame, encodedAudioFrameToAudioFrame, } from './types/EncodedAudioFrame';
import { createDoneEncodedVideoFrame } from './types/EncodedVideoFrame';
export class MediaPacketizer extends EventEmitter {
    mediaPacketFragmenter;
    mediaPacketCounter = 0;
    audioCodec;
    currentAudioLevel;
    currentIsSpeaking;
    qualityHint = {};
    audioBundleFrames = 1;
    audioBundleMaxWaitMs = 0;
    audioBundles = new Map();
    audioPacketsSinceTake = 0;
    videoPacketsSinceTake = 0;
    constructor(options) {
        super();
        this.mediaPacketFragmenter = new MediaPacketFragmenter();
        this.audioCodec = options.audioCodec;
    }
    addVideoFrame(videoFrame, mediaStreamId) {
        this.videoPacketsSinceTake += 1;
        this.emitPacket({
            packetNumber: BigInt(this.mediaPacketCounter++),
            videoFrames: [videoFrame],
            audioFrames: [],
            mediaStreamId,
            endedTracks: [],
            ...this.getSpeechFields(),
            ...this.getQualityHintFields(),
        });
    }
    addAudioFrames(audioFrames, mediaStreamId) {
        if (this.audioBundleFrames <= 1) {
            this.emitAudioPacket(audioFrames, mediaStreamId);
            return;
        }
        let bundle = this.audioBundles.get(mediaStreamId);
        if (!bundle) {
            bundle = { frames: [] };
            this.audioBundles.set(mediaStreamId, bundle);
        }
        bundle.frames.push(...audioFrames);
        if (audioFrames.some((frame) => frame.done) ||
            bundle.frames.length >= this.audioBundleFrames) {
            this.flushAudioBundle(mediaStreamId);
            return;
        }
        // a partial bundle never waits longer than this (mic muted, track
        // ending, encoder stall)
        if (bundle.timer === undefined) {
            bundle.timer = setTimeout(() => {
                this.flushAudioBundle(mediaStreamId);
            }, this.audioBundleMaxWaitMs);
        }
    }
    /**
     * Audio frames per MediaPacket (1 = one packet per frame, the default).
     * Lowering it flushes pending bundles immediately.
     */
    setAudioBundleFrames(frames, maxWaitMs) {
        this.audioBundleFrames = Math.max(1, Math.floor(frames));
        this.audioBundleMaxWaitMs = Math.max(0, maxWaitMs);
        if (this.audioBundleFrames === 1) {
            this.flushAllAudioBundles();
        }
    }
    setSpeechState(isSpeaking, audioLevel) {
        this.currentIsSpeaking = isSpeaking;
        this.currentAudioLevel = audioLevel;
    }
    /** the broadcaster's quality hint, stamped on every packet from now on */
    setQualityHint(hint) {
        this.qualityHint = { ...hint };
    }
    getQualityHintFields() {
        return {
            ...(this.qualityHint.lowQualityPartition !== undefined && {
                lowQualityPartition: this.qualityHint.lowQualityPartition,
            }),
            ...(this.qualityHint.highQualityAvailable !== undefined && {
                highQualityAvailable: this.qualityHint.highQualityAvailable,
            }),
        };
    }
    getSpeechFields() {
        return {
            ...(this.currentAudioLevel !== undefined && {
                audioLevel: this.currentAudioLevel,
            }),
            ...(this.currentIsSpeaking !== undefined && {
                isSpeaking: this.currentIsSpeaking,
            }),
        };
    }
    sendDoneVideoFrame(trackId, frameNumber, mediaStreamId) {
        this.flushAudioBundle(mediaStreamId);
        this.addVideoFrame(createDoneEncodedVideoFrame(trackId, frameNumber), mediaStreamId);
    }
    sendDoneAudioFrame(trackId, frameNumber, mediaStreamId) {
        const doneFrame = createDoneEncodedAudioFrame(trackId, frameNumber);
        // a done frame flushes its bundle immediately (last frame in it)
        this.addAudioFrames([encodedAudioFrameToAudioFrame(doneFrame)], mediaStreamId);
    }
    /** Packets emitted since the previous call. */
    takePacketStats() {
        const stats = {
            audioPackets: this.audioPacketsSinceTake,
            videoPackets: this.videoPacketsSinceTake,
            bundle: this.audioBundleFrames,
        };
        this.audioPacketsSinceTake = 0;
        this.videoPacketsSinceTake = 0;
        return stats;
    }
    destroy() {
        for (const bundle of this.audioBundles.values()) {
            if (bundle.timer !== undefined)
                clearTimeout(bundle.timer);
        }
        this.audioBundles.clear();
        this.removeAllListeners();
    }
    flushAudioBundle(mediaStreamId) {
        const bundle = this.audioBundles.get(mediaStreamId);
        if (!bundle)
            return;
        if (bundle.timer !== undefined)
            clearTimeout(bundle.timer);
        this.audioBundles.delete(mediaStreamId);
        if (bundle.frames.length > 0) {
            this.emitAudioPacket(bundle.frames, mediaStreamId);
        }
    }
    flushAllAudioBundles() {
        for (const mediaStreamId of [...this.audioBundles.keys()]) {
            this.flushAudioBundle(mediaStreamId);
        }
    }
    emitAudioPacket(audioFrames, mediaStreamId) {
        this.audioPacketsSinceTake += 1;
        this.emitPacket({
            packetNumber: BigInt(this.mediaPacketCounter++),
            audioCodec: this.audioCodec,
            videoFrames: [],
            audioFrames,
            mediaStreamId,
            endedTracks: [],
            ...this.getSpeechFields(),
            ...this.getQualityHintFields(),
        });
    }
    emitPacket(mediaPacket) {
        const fragments = this.mediaPacketFragmenter.fragmentMediaPacket(mediaPacket);
        const first = fragments[0];
        if (first && this.listenerCount('packetDescriptor') > 0) {
            this.emit('packetDescriptor', {
                packetNumber: Number(first.packetNumber),
                fragmentCount: fragments.length,
                video: mediaPacket.videoFrames
                    .filter(isRecordableFrame)
                    .map((frame) => ({
                    size: frame.data.byteLength,
                    timestampUs: Number(frame.timestamp),
                    isKeyFrame: frame.isKeyFrame,
                    rotation: frame.rotation,
                    codec: frame.codec,
                    codedWidth: frame.codedWidth,
                    codedHeight: frame.codedHeight,
                    // a copy: the frame's buffer goes to the network
                    extradata: frame.extradata.slice(),
                })),
                audio: mediaPacket.audioFrames
                    .filter(isRecordableFrame)
                    .map((frame) => ({
                    size: frame.data.byteLength,
                    timestampUs: Number(frame.timestamp),
                })),
            });
        }
        for (const fragment of fragments) {
            this.emit('mediaPacketFragment', fragment);
        }
    }
}
